import { FunctionComponent, useCallback } from 'react'
import {
  ActionPopupContentProps,
  ActionPopupTitleProps,
  CommentingActionPopupProps,
} from './types'
import CommentingActionsPopupForm from './CommentingActionPopupForm'
import translate from '@i18n'
import useUser from '@hooks/useUser'
import styled, { css } from 'styled-components'
import AnswerIndicator from './AnswerIndicator'
import { getTitle } from './utils'
import { stripHtml } from '@hooks/useTracking/utils'
import useTracking, { TrackingFnType } from '@hooks/useTracking'

const answerStyle = css`
  position: relative;
  box-sizing: border-box;
  padding-left: 40px;
`

const ActionPopupWrapper = styled.div<{ isEmbed: boolean; isAnswer: boolean }>`
  margin-top: 0.625rem;
  ${({ isAnswer }) => isAnswer && answerStyle}
  ${({ isEmbed }) => isEmbed && 'margin-top: 0;'};
  ${({ isEmbed, isAnswer }) => isEmbed && isAnswer && 'padding-left: 0;'}
`

const deleteStyle = css`
  border: 1px solid ${({ theme }) => theme.colors.grey.grey_70};
  border-top-width: 0;
`

const ActionPopupContent = styled.div<ActionPopupContentProps>`
  box-sizing: border-box;
  padding: 1.25rem;
  background-color: ${({ theme }) => theme.colors.grey.grey_20};

  ${({ actionType }) => actionType === 'delete' && deleteStyle}
`

const ActionPopupTitle = styled.div<ActionPopupTitleProps>`
  padding-bottom: 8px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.grey.grey_30};

  font-family: ${({ theme }) => theme.fonts.goodOT};
  line-height: 1.5;
  font-weight: 600;
  font-size: 16px;

  ${({ theme, actionType }) =>
    (actionType === 'delete' || actionType === 'report') &&
    `color: ${theme.colors.brandColors.brand_55}`}
`

const CommentingActionPopup: FunctionComponent<CommentingActionPopupProps> = ({
  onConfirm,
  onCancel,
  type,
  actionType,
  initialText = '',
}) => {
  const userData = useUser()

  const isEmbed = actionType === 'delete'

  const articleAddCommentHandler = useCallback<TrackingFnType>(
    ({ pageMetadata }) => {
      return {
        event:
          type === 'comment'
            ? 'article_add_comment'
            : 'article_add_comment_answer',
        eventCategory: 'article',
        eventLabel: `${pageMetadata.id}:${stripHtml(pageMetadata.title)}`,
        eventAction: type === 'comment' ? 'add_comment' : 'add_comment_answer',
      }
    },
    [type]
  )

  const handleArticleAddCommentTracking = useTracking(articleAddCommentHandler)

  const handleConfirm = (textValue = '') => {
    handleArticleAddCommentTracking()
    onConfirm(textValue)
  }

  return (
    <ActionPopupWrapper isAnswer={type === 'answer'} isEmbed={isEmbed}>
      {!isEmbed && type === 'answer' && <AnswerIndicator />}
      <ActionPopupContent actionType={actionType}>
        <ActionPopupTitle actionType={actionType}>
          {actionType === 'delete' &&
            translate('commenting.deleteConfirmation')}
          {actionType === 'report' && translate('commenting.reportTitle')}
          {actionType === 'reply' &&
            getTitle({
              name: `${userData?.first_name} ${userData?.last_name}`,
            })}
        </ActionPopupTitle>
        <CommentingActionsPopupForm
          actionType={actionType}
          onConfirm={handleConfirm}
          onCancel={onCancel}
          textInput={initialText}
        />
      </ActionPopupContent>
    </ActionPopupWrapper>
  )
}

export default CommentingActionPopup
